<script setup lang="ts">
import { computed, type CSSProperties } from 'vue';

export interface PlsButton1Props {
  borderColor?: string;
  backgroundColor?: string;
  text?: string;
  /** When using the default slot instead of `text`, set for screen readers. */
  ariaLabel?: string;
  /** Exposes busy state to assistive tech (e.g. while submitting). */
  ariaBusy?: boolean;
  textColor?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<PlsButton1Props>(), {
  text: '',
  ariaLabel: '',
  borderColor: 'transparent',
  backgroundColor: 'transparent',
  textColor: '#000000',
  disabled: false,
});

const emit = defineEmits<{
  (event: 'click'): void;
}>();

const computedStyles = computed<CSSProperties>(() => {
  return {
    borderColor: props.borderColor,
    backgroundColor: props.backgroundColor,
    color: props.textColor,
  };
});

const handleClick = () => {
  if (!props.disabled) {
    emit('click');
  }
};

const accessibleLabel = computed(() => props.ariaLabel || props.text || undefined);
</script>

<template>
  <button
    :class="['pls-button1', 'theme-font-main', 'btn-radius']"
    :style="computedStyles"
    :disabled="disabled"
    :aria-label="accessibleLabel"
    :aria-busy="ariaBusy ? 'true' : undefined"
    tabindex="0"
    @click="handleClick"
  >
    <!-- Render text or slot -->
    <template v-if="text">
      {{ text }}
    </template>
    <slot v-else />
  </button>
</template>

<style scoped lang="scss">
.pls-button1 {
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
  width: 100%;
  border-style: solid;
  border-width: 1px;
  padding: 12px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
}
</style>
